import { defineMessages, IntlFormatters } from "react-intl";
import { IColorPalette } from "../../front/store/user/models";
import { IPitch } from "../types";
import { solfaParser } from "./solfaParser";

export const solfaNameParser = ["f", "d", "s", "r", "l", "m", "t"];
export const solfaNameParserEt = ["n", "j", "s", "l", "r", "m", "d"];
export const isEnglishSolfa = (locale: string) =>
    locale === "en" || locale === "en-us" || locale === "en-gb" || locale === "pl" || locale === "ga";

export const i18nSolfa = (solfa: string, locale: string, solfaKam: boolean) => {
    // Japanese: render solfa in katakana (relative do)
    if (locale === "ja" || locale === "ja-jp") {
        // base degree
        const base = solfa.slice(0, 1);
        const baseJa =
            base === "d" ? "ド" :
                base === "r" ? "レ" :
                    base === "m" ? "ミ" :
                        base === "f" ? "ファ" :
                            base === "s" ? "ソ" :
                                base === "l" ? "ラ" :
                                    base === "t" ? "シ" :
                                        base;

        // accidental marker in your system: i = sharp, a/o/u = flat-ish
        const acc = solfa.slice(1, 2);
        const accidental =
            acc === "i" ? "#" :
                acc === "a" || acc === "o" || acc === "u" ? "b" :
                    "";

        // octave marks: , ’ ’’
        const octave = solfa.slice(2);

        return `${baseJa}${accidental}${octave}`;
    }

    if (locale === "et") {
        let solfaEt = solfa;
        for (let s = 0; s < 7; s++) {
            if (solfaEt.indexOf(solfaNameParser[s]) !== -1) {
                solfaEt = solfaEt.replace(solfaNameParser[s], solfaNameParserEt[s]);
                break;
            }
        }
        solfaEt = solfaEt.replace("a", "u");
        solfaEt = solfaEt.replace("o", "u");
        return solfaEt;
    }
    if (locale === "lt") {
        if (solfa.slice(1, 2) === "i") {
            return solfa.replace("i", "#");
        } else if (solfa.slice(1, 2) === "a" || solfa.slice(1, 2) === "o") {
            return solfa.replace("a", "<i>b</i>").replace("o", "<i>b</i>");
        }
    }
    if (isEnglishSolfa(locale)) {
        if (solfaKam) {
            return solfa.replace("ta", "tu").replace("lo", "lu").replace("sa", "su").replace("ma", "mu").replace("ra", "ru");
        } else {
            return solfa.replace("ta", "te").replace("lo", "le").replace("sa", "se").replace("ma", "me");
        }
    }
    return solfa;
};

export const solfaNameOptions = (locale: string, solfaKam: boolean) =>
    solfas.map(solfa => {
        let value = ((solfaNameParser.indexOf(solfa.slice(0, 1)) - 1) * 7) % 12;
        if (value < 0) value += 12;
        if (solfa.slice(-1) === ",") value -= 12;
        if (solfa.slice(-1) === "’") value += 12;
        if (solfa.slice(-2, -1) === "’") value += 12;

        return {
            value: solfa,
            label: i18nSolfa(solfa, locale, solfaKam),
        };
    });


export const solfas = [
    "d,",
    "di,",
    "ra,",
    "r,",
    "ri,",
    "ma,",
    "m,",
    "f,",
    "fi,",
    "sa,",
    "s,",
    "si,",
    "lo,",
    "l,",
    "li,",
    "ta,",
    "t,",
    "d",
    "di",
    "ra",
    "r",
    "ri",
    "ma",
    "m",
    "f",
    "fi",
    "sa",
    "s",
    "si",
    "lo",
    "l",
    "li",
    "ta",
    "t",
    "d’",
    "di’",
    "ra’",
    "r’",
    "ri’",
    "ma’",
    "m’",
    "f’",
    "fi’",
    "sa’",
    "s’",
    "si’",
    "lo’",
    "l’",
    "li’",
    "ta’",
    "t’",
    "d’’",
];

export const getSolfaName = (p: IPitch, locale: string) => {
    return (
        solfaNameParser[p.s] +
        (locale === "lt"
            ? (p.a === -1 ? "b" : p.a === 1 ? "#" : "")
            : (p.a === 1 ? "i" : p.a === -1 ? (locale === "et" ? "u" : p.s === 4 ? "o" : "a") : "")
        ) +
        (p.o === -1 ? "," : p.o === 1 ? "’" : p.o === 2 ? "’’" : "")
    );
};

const solfaNamesBase = ["d", "r", "m", "f", "s", "l", "t"];
const solfaNamesBaseEt = ["j", "l", "m", "n", "s", "r", "d"];
const solfaNamesBaseJa = ["ド", "レ", "ミ", "ファ", "ソ", "ラ", "シ"];

export const solfaNamesArray = (locale: string) => {
    const base = locale === "et" ? solfaNamesBaseEt : locale === 'ja' ? solfaNamesBaseJa : solfaNamesBase;
    return [...base.map(sf => `${sf},`), ...base, ...base.map(sf => `${sf}’`), ...base.map(sf => `${sf}’’`)];
};

export const isPitchPulsating = ({ i }: { i: number }, p: IPitch) => {
    if (!p) return false;
    const o = i < 7 ? -1 : i === 21 ? 2 : i >= 14 ? 1 : 0;
    const s = solfaParser[i % 7];
    if (p.s === s && (p.o || 0) === o) return true;
    return false;
};


const colorsThemeValues = [4, 1, 2, 3];

export const solfaColorsOptions = (fm: IntlFormatters["formatMessage"]) =>
    colorsThemeValues.map(value => ({
        value,
        label: fm(
            defineMessages({
                message: {
                    id: `score.settings.colors.${value}`,
                },
            }).message
        ),
    }));

const green = "#279324";
const yellow = "#DBCC19";
const blue = "#3168F4";
const grey = "#999999";
const red = "#F42539";
const purple = "#9F51F2";
const orange = "#FF8939";
const black = "#090F19";
const brown = "#A85F29";

const colors1 = {
    d: green,
    r: yellow,
    m: blue,
    f: grey,
    s: red,
    l: purple,
    t: orange,
};

const colors2 = {
    d: black,
    r: orange,
    m: blue,
    f: brown,
    s: red,
    l: green,
    t: purple,
};

const colors3 = {
    d: blue,
    r: green,
    m: orange,
    f: purple,
    s: red,
    l: black,
    t: yellow,
};

const colors4 = {
    d: "#FF1A1A",
    r: "#FF851B",
    m: "#E2D100",
    f: "#B1D322",
    s: "#00A77A",
    l: "#7951A8",
    t: "#F24099",
};

export const colorThemes: { [prop: string]: IColorPalette["colors"] } = {
    colors1,
    colors2,
    colors3,
    colors4,
};

export const getColors = (colors: number | IColorPalette) => {
    return typeof colors === "number" ? colorThemes[`colors${colors}`] : colors.colors;
};

const getLongVersion = (solfa: string, locale: string) => {
    if (locale === "et") {
        switch (solfa) {
            case "j":
                return "jo";
            case "l":
                return "le";
            case "m":
                return "mi";
            case "n":
                return "na";
            case "s":
                return "so";
            case "r":
                return "ra";
            case "d":
                return "di";
        }
    } else if (locale === "hu") {
        switch (solfa) {
            case "d":
                return "dó";
            case "r":
                return "ré";
            case "m":
                return "mi";
            case "f":
                return "fá";
            case "s":
                return "szó";
            case "l":
                return "lá";
            case "t":
                return "ti";
        }
    } else {
        switch (solfa) {
            case "d":
                return "do";
            case "r":
                return "re";
            case "m":
                return "mi";
            case "f":
                return "fa";
            case "s":
                return "sol";
            case "l":
                return "la";
            case "t":
                return locale === "lt" ? "si" : "ti";
        }
    }
}

export const lengthenSolfa = (solfa: string, locale: string, solfaShort: boolean) => {
    if (solfaShort && locale !== "lt") return solfa;
    const withoutOctave = /\w+/.exec(solfa)![0];
    if (withoutOctave === "si" && locale === "hu") {
        return "szi" + solfa.slice(2);
    }
    if (withoutOctave === "sa" && locale === "hu") {
        return "sza" + solfa.slice(2);
    }
    if (withoutOctave.length !== 1 && locale !== "lt") return solfa;
    let newWithoutOctave = getLongVersion(withoutOctave.slice(0, 1), locale);
    return newWithoutOctave + solfa.slice(1);
};

export const getEnharmonicVersion = (pitch: IPitch): IPitch => {
    return {
        ...pitch,
        s: (pitch.s + pitch.a * 2) % 7 as IPitch["s"],
        a: -pitch.a as IPitch["a"],
    }
}